import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseQueryWithAuth } from './utils';

type GraphQLRequest = {
  document: string;
  variables?: Record<string, unknown>;
};

type GraphQLErrorBody = { errors: { message: string }[] };

const hasGraphQLErrors = (body: unknown): body is GraphQLErrorBody =>
  typeof body === 'object' &&
  body !== null &&
  'errors' in body &&
  Array.isArray(body.errors) &&
  body.errors.length > 0;

const hasGraphQLData = (body: unknown): body is { data: unknown } =>
  typeof body === 'object' && body !== null && 'data' in body;

// GraphQL reports failures in a 200 response, so errors must be lifted out of
// the body or a failed query reads as an empty result.
const graphqlBaseQuery: BaseQueryFn<
  GraphQLRequest,
  unknown,
  FetchBaseQueryError
> = async ({ document, variables }, api, extraOptions) => {
  const result = await baseQueryWithAuth(
    {
      url: '/v2/graphql',
      method: 'POST',
      body: { query: document, variables },
    },
    api,
    extraOptions
  );
  if (result.error) return { error: result.error };

  const body = result.data;
  if (hasGraphQLErrors(body)) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: body.errors.map(({ message }) => message).join('\n'),
        data: body,
      },
    };
  }
  if (!hasGraphQLData(body)) {
    return {
      error: {
        status: 'PARSING_ERROR',
        originalStatus: 200,
        data: '',
        error: 'No GraphQL data',
      },
    };
  }
  return { data: body.data };
};

export type GroupEntity = {
  id: string;
  name: string;
  mrid: string;
};

type GroupEntityPage = {
  entities: GroupEntity[];
  nextCursor: string | null;
};

type NetworkTopologyResponse<Page> = {
  networkTopology: {
    groupTypes: {
      name: string;
      entities: Page;
    }[];
  } | null;
};

export const DISTRICT_GROUP_TYPE = 'district';
export const CIRCUIT_GROUP_TYPE = 'circuit';

export type GroupType = typeof DISTRICT_GROUP_TYPE | typeof CIRCUIT_GROUP_TYPE;

export type GroupProgressOrderBy = 'MILES_LEFT' | 'MILES_COMPLETED';

export type WorkProgress = {
  summary: {
    lineMiles: number;
    spanCount: number;
    networkShare: number;
  };
  milesByProgram: {
    program: string | null;
    lineMiles: number;
  }[];
};

export type GroupProgress = {
  entity: GroupEntity & { bbox: number[] | null };
  milesCompleted: number;
  milesLeft: number;
  completedShare: number;
};

type GroupProgressPage = {
  rows: GroupProgress[];
  totalCount: number;
};

type WorkProgressArgs = {
  clientCode: string;
  groupEntityIds: string[];
};

type AreaArgs = WorkProgressArgs & {
  groupType: GroupType;
};

type GroupProgressArgs = AreaArgs & {
  orderBy: GroupProgressOrderBy;
};

type CircuitPagesArgs = {
  clientCode: string;
  districtIds: string[];
};

export const GROUP_PROGRESS_PAGE_SIZE = 50;
const CIRCUITS_PAGE_SIZE = 100;

const toGroupEntityFilter = (groupEntityIds: string[]) =>
  groupEntityIds.length > 0 ? { anyGroupEntityIds: groupEntityIds } : null;

const WORK_PROGRESS_QUERY = `
  query WorkProgress($clientCode: String!, $groupEntities: GroupEntityFilter) {
    workProgress(clientCode: $clientCode, groupEntities: $groupEntities) {
      summary {
        lineMiles
        spanCount
        networkShare
      }
      milesByProgram {
        program
        lineMiles
      }
    }
  }
`;

const GROUP_PROGRESS_QUERY = `
  query GroupProgressPage(
    $clientCode: String!
    $groupType: String!
    $groupEntities: GroupEntityFilter
    $orderBy: GroupProgressOrderBy!
    $limit: Int!
    $offset: Int!
  ) {
    groupProgress(
      clientCode: $clientCode
      groupType: $groupType
      groupEntities: $groupEntities
      orderBy: $orderBy
      direction: DESC
      limit: $limit
      offset: $offset
    ) {
      totalCount
      rows {
        entity {
          id
          name
          mrid
          bbox
        }
        milesCompleted
        milesLeft
        completedShare
      }
    }
  }
`;

const CIRCUITS_QUERY = `
  query Circuits(
    $clientCode: String!
    $within: [UUID!]
    $limit: Int!
    $cursor: String
  ) {
    networkTopology(clientCode: $clientCode) {
      groupTypes {
        name
        entities(within: $within, limit: $limit, cursor: $cursor) {
          entities {
            id
            name
            mrid
          }
          nextCursor
        }
      }
    }
  }
`;

const DISTRICTS_QUERY = `
  query Districts($clientCode: String!) {
    networkTopology(clientCode: $clientCode) {
      groupTypes {
        name
        entities(limit: 500) {
          entities {
            id
            name
            mrid
          }
        }
      }
    }
  }
`;

type GroupCompletion = { mrid: string; completedShare: number };

const GROUP_COMPLETION_QUERY = `
  query GroupCompletion(
    $clientCode: String!
    $groupType: String!
    $groupEntities: GroupEntityFilter
  ) {
    groupCompletion(
      clientCode: $clientCode
      groupType: $groupType
      groupEntities: $groupEntities
    ) {
      mrid
      completedShare
    }
  }
`;

export const bosunApi = createApi({
  reducerPath: 'bosunApi',
  baseQuery: graphqlBaseQuery,
  endpoints: (builder) => ({
    getDistricts: builder.query<GroupEntity[], string>({
      query: (clientCode) => ({
        document: DISTRICTS_QUERY,
        variables: { clientCode },
      }),
      transformResponse: ({
        networkTopology,
      }: NetworkTopologyResponse<{ entities: GroupEntity[] }>) =>
        networkTopology?.groupTypes.find(
          ({ name }) => name === DISTRICT_GROUP_TYPE
        )?.entities.entities ?? [],
    }),
    getWorkProgress: builder.query<WorkProgress | null, WorkProgressArgs>({
      query: ({ clientCode, groupEntityIds }) => ({
        document: WORK_PROGRESS_QUERY,
        variables: {
          clientCode,
          groupEntities: toGroupEntityFilter(groupEntityIds),
        },
      }),
      transformResponse: ({
        workProgress,
      }: {
        workProgress: WorkProgress | null;
      }) => workProgress,
    }),
    groupProgressPages: builder.infiniteQuery<
      GroupProgressPage,
      GroupProgressArgs,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastOffset) => {
          const nextOffset = lastOffset + GROUP_PROGRESS_PAGE_SIZE;
          return lastPage.rows.length < GROUP_PROGRESS_PAGE_SIZE ||
            nextOffset >= lastPage.totalCount
            ? undefined
            : nextOffset;
        },
      },
      query: ({
        queryArg: { clientCode, groupType, groupEntityIds, orderBy },
        pageParam,
      }) => ({
        document: GROUP_PROGRESS_QUERY,
        variables: {
          clientCode,
          groupType,
          groupEntities: toGroupEntityFilter(groupEntityIds),
          orderBy,
          limit: GROUP_PROGRESS_PAGE_SIZE,
          offset: pageParam,
        },
      }),
      transformResponse: ({
        groupProgress,
      }: {
        groupProgress: GroupProgressPage;
      }) => groupProgress,
    }),
    circuitPages: builder.infiniteQuery<
      GroupEntityPage,
      CircuitPagesArgs,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: ({ nextCursor }) => nextCursor ?? undefined,
      },
      query: ({ queryArg: { clientCode, districtIds }, pageParam }) => ({
        document: CIRCUITS_QUERY,
        variables: {
          clientCode,
          within: districtIds,
          limit: CIRCUITS_PAGE_SIZE,
          cursor: pageParam,
        },
      }),
      transformResponse: ({
        networkTopology,
      }: NetworkTopologyResponse<GroupEntityPage>) =>
        networkTopology?.groupTypes.find(
          ({ name }) => name === CIRCUIT_GROUP_TYPE
        )?.entities ?? { entities: [], nextCursor: null },
    }),
    getGroupCompletion: builder.query<GroupCompletion[], AreaArgs>({
      query: ({ clientCode, groupType, groupEntityIds }) => ({
        document: GROUP_COMPLETION_QUERY,
        variables: {
          clientCode,
          groupType,
          groupEntities: toGroupEntityFilter(groupEntityIds),
        },
      }),
      transformResponse: ({
        groupCompletion,
      }: {
        groupCompletion: GroupCompletion[];
      }) => groupCompletion,
    }),
  }),
});

export const {
  useGetDistrictsQuery,
  useGetWorkProgressQuery,
  useGroupProgressPagesInfiniteQuery,
  useCircuitPagesInfiniteQuery,
  useGetGroupCompletionQuery,
} = bosunApi;
export const resetBosunApiState = bosunApi.util.resetApiState;
